/**
 * src/features/account/useReveal.js
 *
 * Step-up authentication hook for revealing masked PII fields.
 *
 * Flow:
 *  1. User clicks "Reveal" — triggerReveal() is called.
 *  2. A SweetAlert2 password prompt is shown.
 *  3. POST /api/users/me/reveal is called with the password.
 *  4. On success the plain values are stored in component state and a
 *     5-minute auto-clear timer starts.
 *  5. After 5 minutes (or on explicit revoke) the values are cleared and
 *     the fields go back to masked display.
 *
 * Design: one-time response — the backend never stores a reveal session.
 * Each reveal cycle requires a fresh password confirmation.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import Swal from "../../utils/apparifySwal";
import { userAPI } from "../../services/profileService";

const REVEAL_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * @param {object} opts
 * @param {object}  opts.swalBase  – shared SweetAlert2 base config
 */
export function useReveal({ swalBase } = {}) {
  // revealed holds { email, phone, birthdate } or null
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const timerRef = useRef(null);

  const swalBaseRef = useRef(swalBase);
  useEffect(() => { swalBaseRef.current = swalBase; }, [swalBase]);

  /** Clear revealed values and cancel any pending timer */
  const revokeReveal = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setRevealed(null);
  }, []);

  /** Arm the auto-clear timer (resets if called again) */
  const armTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(revokeReveal, REVEAL_TTL_MS);
  }, [revokeReveal]);

  /** Prompt the user for their password and call the reveal endpoint */
  const triggerReveal = useCallback(async () => {
    const base = swalBaseRef.current || {};

    const { value: password, isConfirmed } = await Swal.fire({
      ...base,
      title: "CONFIRM YOUR IDENTITY",
      html:
        '<p class="mb-2 text-sm text-neutral-500 dark:text-neutral-400">Enter your password to reveal your email, phone, and birthdate for 5 minutes.</p>' +
        '<div class="relative mx-auto mt-4 w-[calc(100%-2em)]">' +
          '<input data-reveal-password type="password" autocomplete="current-password" aria-label="Password" placeholder="Your password" ' +
            'class="w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 pr-16 text-sm text-neutral-900 outline-none transition focus:border-neutral-500 focus:ring-2 focus:ring-neutral-200 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-500 dark:focus:ring-neutral-800" />' +
          '<button data-reveal-password-toggle type="button" aria-label="Show password" aria-pressed="false" ' +
            'class="absolute inset-y-0 right-0 px-4 text-xs font-semibold text-neutral-500 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-neutral-400 dark:text-neutral-400 dark:hover:text-neutral-100">Show</button>' +
        '</div>',
      showCancelButton: true,
      confirmButtonText: "REVEAL",
      cancelButtonText: "CANCEL",
      didOpen: () => {
        const popup = Swal.getPopup();
        const input = popup?.querySelector("[data-reveal-password]");
        const toggle = popup?.querySelector("[data-reveal-password-toggle]");
        if (!input || !toggle) return;

        toggle.addEventListener("click", () => {
          const makeVisible = input.type === "password";
          input.type = makeVisible ? "text" : "password";
          toggle.textContent = makeVisible ? "Hide" : "Show";
          toggle.setAttribute("aria-label", makeVisible ? "Hide password" : "Show password");
          toggle.setAttribute("aria-pressed", String(makeVisible));
          input.focus({ preventScroll: true });
        });
        input.focus({ preventScroll: true });
      },
      preConfirm: () => {
        const input = Swal.getPopup()?.querySelector("[data-reveal-password]");
        const val = input?.value || "";
        if (!val || !val.trim()) {
          Swal.showValidationMessage("Password is required");
          return false;
        }
        return val;
      },
    });

    if (!isConfirmed || !password) return;

    setRevealing(true);
    try {
      const data = await userAPI.reveal({ password });
      setRevealed({
        email:     data?.email     ?? null,
        phone:     data?.phone     ?? null,
        birthdate: data?.birthdate ?? null,
      });
      armTimer();

      await Swal.fire({
        ...base,
        icon: "success",
        title: "REVEALED",
        html: "Your details are visible for <strong>5 minutes</strong>. They will be hidden automatically.",
        timer: 3000,
        showConfirmButton: false,
      });
    } catch (err) {
      const rawMessage = err?.message || "Could not verify identity.";
      const errorCode = err?.response?.data?.code;
      const isIncorrectPassword =
        errorCode === "INVALID_PASSWORD" ||
        (err?.status === 401 && /incorrect password/i.test(rawMessage));
      const msg = isIncorrectPassword ? "Incorrect password." : rawMessage;
      const isRateLimit = msg.includes("Too many");
      await Swal.fire({
        ...base,
        icon: "error",
        title: isRateLimit ? "TOO MANY ATTEMPTS" : "VERIFICATION FAILED",
        html: msg,
        confirmButtonText: "OK",
      });
    } finally {
      setRevealing(false);
    }
  }, [armTimer]);

  // Clean up the timer on unmount
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return {
    /** Object with { email, phone, birthdate } when revealed, else null */
    revealed,
    /** True while the reveal request is in-flight */
    revealing,
    /** Open the password prompt and attempt step-up auth */
    triggerReveal,
    /** Manually revoke early */
    revokeReveal,
    /** Convenience: true when at least one field is available */
    isRevealed: revealed !== null,
  };
}
